import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Loader2,
  ShieldCheck,
  Users,
  CreditCard,
  Mail,
  Send,
  Clock,
  BookOpen,
  BookOpenCheck,
  LifeBuoy,
  Building2,
  Activity,
  LogOut
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Section registry. Items with a `to` are wired; the rest are scaffolded placeholders.
const SECTIONS = [
  { id: 'books', label: 'Book Availability', Icon: BookOpen, phase: 'ADMIN.1', to: '/admin/books' },
  { id: 'users', label: 'Users & Accounts', Icon: Users, phase: 'ADMIN.2' },
  { id: 'revenue', label: 'Revenue & Purchases', Icon: CreditCard, phase: 'ADMIN.3' },
  { id: 'communications', label: 'Communications Health', Icon: Mail, phase: 'ADMIN.4' },
  { id: 'telegram', label: 'Telegram Ops', Icon: Send, phase: 'ADMIN.5' },
  { id: 'jobs', label: 'Scheduled Jobs', Icon: Clock, phase: 'ADMIN.6' },
  { id: 'content', label: 'Book Content Editor', Icon: BookOpenCheck, phase: 'ADMIN.7' },
  { id: 'support', label: 'Support & Recovery', Icon: LifeBuoy, phase: 'ADMIN.8' },
  { id: 'teams', label: 'Team & B2B', Icon: Building2, phase: 'ADMIN.9' },
  { id: 'system', label: 'System Health', Icon: Activity, phase: 'ADMIN.10' }
];

const AdminPage = () => {
  const { isAuthenticated, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login?redirect=/admin', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        const res = await fetch(`${API_URL}/api/admin/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.status === 401) {
          navigate('/login?redirect=/admin', { replace: true });
          return;
        }
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }
        const data = await res.json();
        if (!data.is_admin) {
          // Not an admin — silently redirect away (no hint this page exists)
          navigate('/dashboard', { replace: true });
          return;
        }
        setAdminInfo(data);
      } catch (e) {
        setError(e.message || 'Failed to verify admin access');
      } finally {
        setChecking(false);
      }
    };

    if (isAuthenticated) checkAdmin();
  }, [isAuthenticated, navigate]);

  if (authLoading || checking) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-8">
        <div className="max-w-md bg-red-500/10 border border-red-500/30 text-red-300 rounded-xl p-6 text-center" data-testid="admin-error">
          <p className="font-semibold mb-2">Admin access error</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!adminInfo) return null;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      {/* Minimal header — intentionally not using dashboard shell/sidebar */}
      <header className="border-b border-zinc-800 bg-zinc-950/95 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight" data-testid="admin-heading">Admin Console</h1>
              <p className="text-xs text-zinc-500">Signed in as {adminInfo.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/dashboard"
              className="text-sm text-zinc-400 hover:text-white transition-colors"
              data-testid="admin-back-to-dashboard"
            >
              Back to dashboard
            </Link>
            <button
              onClick={() => {
                logout();
                navigate('/login', { replace: true });
              }}
              className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white transition-colors"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" />
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8 bg-gradient-to-br from-red-500/10 via-purple-500/10 to-transparent border border-zinc-800 rounded-2xl p-6">
          <p className="text-xs uppercase tracking-wider text-red-400 font-semibold mb-1">ADMIN.0 Foundation</p>
          <h2 className="text-2xl font-bold text-white mb-2">Welcome, Paul.</h2>
          <p className="text-zinc-400 max-w-2xl">
            This is the hidden admin console. Access is gated by the <code className="text-zinc-300 bg-zinc-900 px-1.5 py-0.5 rounded text-sm">is_admin</code> flag on your user account and the
            {' '}<code className="text-zinc-300 bg-zinc-900 px-1.5 py-0.5 rounded text-sm">ADMIN_BOOTSTRAP_EMAILS</code> env allowlist.
            The sections below are scaffolded for future phases. None are wired yet — we'll build them one at a time.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-sections-grid">
          {SECTIONS.map(({ id, label, Icon, phase, to }) => {
            const isActive = !!to;
            const content = (
              <>
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${
                    isActive
                      ? 'bg-red-500/10 border-red-500/30'
                      : 'bg-zinc-800 border-zinc-700'
                  }`}>
                    <Icon className={`w-5 h-5 ${isActive ? 'text-red-400' : 'text-zinc-400'}`} />
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold bg-zinc-800/80 rounded-full px-2 py-1">
                    {phase}
                  </span>
                </div>
                <h3 className="text-white font-semibold mb-1">{label}</h3>
                <p className={`text-sm ${isActive ? 'text-red-300' : 'text-zinc-500'}`}>
                  {isActive ? 'Open →' : 'Coming soon'}
                </p>
              </>
            );

            if (isActive) {
              return (
                <Link
                  key={id}
                  to={to}
                  className="group bg-zinc-900/80 border border-zinc-800 hover:border-red-500/40 hover:bg-zinc-900 rounded-xl p-5 transition-all"
                  data-testid={`admin-section-${id}`}
                >
                  {content}
                </Link>
              );
            }
            return (
              <div
                key={id}
                className="group bg-zinc-900/60 border border-zinc-800 rounded-xl p-5 opacity-60 cursor-not-allowed select-none"
                data-testid={`admin-section-${id}`}
                aria-disabled="true"
                title="Coming in a later phase"
              >
                {content}
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-xs text-zinc-600 border-t border-zinc-900 pt-6">
          Access model: role flag on user doc + magic-link auth. API base: <code>/api/admin/*</code>.
          First admin seeded via <code>ADMIN_BOOTSTRAP_EMAILS</code> on backend startup.
        </div>
      </main>
    </div>
  );
};

export default AdminPage;
